import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import { CITIES_DATA } from "@/lib/data";

// 4 flagship destinations (deep SEO content, prioritary leads)
const TOP_SLUGS = ["saint-malo", "dinard", "cancale", "dinan"];

const TopCitiesShowcase = () => {
  const cities = TOP_SLUGS.map((slug) => CITIES_DATA[slug]).filter(Boolean);

  return (
    <section
      className="py-20 lg:py-28 bg-cream relative overflow-hidden"
      data-testid="top-cities-showcase"
    >
      {/* Decorative orb */}
      <div
        aria-hidden="true"
        className="absolute -top-20 right-0 w-[400px] h-[400px] rounded-full pointer-events-none opacity-40"
        style={{
          background:
            "radial-gradient(circle, rgba(197,138,63,0.15) 0%, transparent 65%)",
        }}
      />

      <div className="container-tm relative">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 mb-14 items-end">
          <div className="lg:col-span-7">
            <p className="overline mb-4">Nos 4 destinations phares</p>
            <h2 className="font-serif font-light text-ink text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Les villes où nous sommes
              <br />
              <em className="text-gold">les plus reconnus.</em>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="font-sans text-base md:text-lg text-ink/65 leading-relaxed">
              4 communes à fort potentiel locatif où nos équipes interviennent
              quotidiennement. Découvrez nos pages dédiées avec étude SEO,
              tarifs locaux, FAQ et études de revenus.
            </p>
          </div>
        </div>

        {/* Cards : 2 large + 2 medium */}
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {cities.map((c, i) => (
            <Link
              key={c.slug}
              to={`/villes/${c.slug}`}
              data-testid={`top-city-${c.slug}`}
              className="group relative block overflow-hidden rounded-3xl shadow-soft border border-ink/5 hover:shadow-lift transition-all duration-700"
            >
              {/* Background image */}
              <div className="aspect-[4/3] lg:aspect-[16/10] overflow-hidden bg-ink relative">
                <img
                  src={c.heroImage}
                  alt={`Conciergerie Airbnb à ${c.name}, Bretagne`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 ease-out"
                />
                {/* Dark gradient overlay for legibility */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(to top, rgba(11,26,38,0.92) 0%, rgba(11,26,38,0.55) 35%, transparent 65%)",
                  }}
                  aria-hidden="true"
                />

                {/* Top-right rank badge */}
                <span
                  className="absolute top-5 right-5 inline-flex items-center gap-1.5 bg-gold text-ink text-[10px] uppercase tracking-[0.22em] font-semibold rounded-full px-3 py-1.5 shadow-soft"
                >
                  <Star size={10} fill="currentColor" strokeWidth={0} />
                  Top {i + 1}
                </span>

                {/* Content overlay */}
                <div className="absolute inset-x-0 bottom-0 p-7 lg:p-10 text-cream">
                  <p className="text-[10px] uppercase tracking-[0.32em] text-gold font-semibold mb-3">
                    Conciergerie Airbnb
                  </p>
                  <h3 className="font-serif italic font-light text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight mb-4">
                    {c.name}
                  </h3>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                    <span className="text-cream/80">
                      ⌀ <strong className="text-cream">{c.intro.avgPrice}</strong>{" "}
                      / nuit
                    </span>
                    <span className="text-cream/55">·</span>
                    <span className="text-cream/80">
                      {c.intro.travelers} voyageurs/an
                    </span>
                    <span className="text-cream/55 hidden md:inline">·</span>
                    <span className="text-cream/80 hidden md:inline">
                      {c.intro.tgv}
                    </span>
                  </div>

                  <span className="mt-6 inline-flex items-center gap-2 text-gold font-sans text-[11px] uppercase tracking-[0.22em] font-semibold group-hover:gap-3 transition-all duration-500">
                    Voir la page {c.name}
                    <ArrowRight size={13} strokeWidth={2} />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <p className="mt-10 text-center text-ink/55 text-sm">
          ↓ Découvrez plus bas <strong>nos 26 autres villes</strong> sur la carte
          interactive et par secteur.
        </p>
      </div>
    </section>
  );
};

export default TopCitiesShowcase;
